import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { Store, select } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Asset } from "contentful";
import { Observable, Subject, of } from "rxjs";
import { catchError, startWith, switchMap, take, tap, withLatestFrom } from "rxjs/operators";
import { PLUGIN } from "src/app/common/enums/plugins";
import { MODULES_CRM_IDS_MAP } from "src/app/core/constants/modules-crm-ids-map";
import { CrmModule } from "src/app/core/enums/permissions";
import { SalesInfoEntry } from "src/app/core/interfaces/contentful";
import { IContactPerson } from "src/app/core/interfaces/project-entity";
import { DynamicSnackBar } from "src/app/core/material/snack-bar/snack-bar";
import { AnalyticsService } from "src/app/core/services/analytics/analytics.service";
import { DialogsService } from "src/app/core/services/dialogs.service";
import { FilesService } from "src/app/core/services/files.service";
import { MarketingCrmService } from "src/app/core/services/marketing-crm.service";
import { takeLatestFrom } from "src/app/core/utils/ngrx";
import { RootState } from "src/app/ngrx/root-reducers";
import { selectEntity, selectRegionServiceContactPerson, selectUserInfo } from "src/app/ngrx/selectors/app.selectors";
import { MARKETING_CRM_GUID_MAP } from "./constants/marketing-crm-uid-map";

import { LeadService } from "./lead.service";

interface ResolvedSalesInfoEntry {
  moduleName: string;
  headline1: string;
  text1: string;
  headline2: string;
  text2: string;
  action?: string;
  media?: Asset;
  agb?: string;
}
@Component({
  selector: "app-lead-dialog",
  templateUrl: "./lead.component.html",
  styleUrls: ["./lead.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LeadComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("teaserForm", { static: false, read: ElementRef }) public teaserForm: ElementRef;

  public translationParams: { moduleName: string };
  public agreementTranslationParams: { url: string };

  public leadForm: FormGroup;
  public communicationTypes = [
    {
      id: "phone",
      key: "LEAD.USE_PHONE"
    },
    {
      id: "email",
      key: "LEAD.USE_EMAIL"
    }
  ];

  public readonly contactPerson$: Observable<IContactPerson>;
  public contentfulInfo$ = new Observable<ResolvedSalesInfoEntry>();

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: { slot: PLUGIN; salesInfo: SalesInfoEntry; hasAgreementCheck?: boolean },
    public readonly dialogRef: MatDialogRef<LeadComponent>,
    private readonly fb: FormBuilder,
    private readonly leadService: LeadService,
    private readonly snackbar: DynamicSnackBar,
    private readonly store: Store<RootState>,
    private readonly dialogsService: DialogsService,
    private readonly filesService: FilesService,
    private readonly translate: TranslateService,
    private readonly analytics: AnalyticsService,
    private readonly marketingService: MarketingCrmService
  ) {
    this.contactPerson$ = this.getContactPerson(MODULES_CRM_IDS_MAP[this.data.slot]);
  }

  public get communicationType() {
    return this.leadForm.get("communicationType");
  }

  private get phone() {
    return this.leadForm.get("phoneNumber");
  }

  private get email() {
    return this.leadForm.get("email");
  }

  private get itsMyPhone() {
    return this.leadForm.get("itsMyPhone");
  }

  public ngOnInit(): void {
    const resolveContentfulType = (entry: SalesInfoEntry): ResolvedSalesInfoEntry => {
      const module = entry.fields as any;
      return {
        ...module,
        moduleName: `LAUNCHPAD.PLUGINS.${this.data.slot}.name`,
        text1: documentToHtmlString(module.text1),
        text2: documentToHtmlString(module.text2),
        action: module.action?.fields?.url || null,
        agb: module.agb || null
      };
    };

    this.translationParams = { moduleName: this.translate.instant(`LAUNCHPAD.PLUGINS.${this.data.slot}.name`) };
    this.agreementTranslationParams = { url: resolveContentfulType(this.data.salesInfo).agb };
    this.contentfulInfo$ = of(resolveContentfulType(this.data.salesInfo));

    const selectedUser = takeLatestFrom(this.store, selectUserInfo);
    const selectedEntity = takeLatestFrom(this.store, selectEntity);

    this.leadForm = this.fb.group({
      moduleId: MODULES_CRM_IDS_MAP[this.data.slot] ?? this.data.slot,
      email: [{ value: selectedUser?.email, disabled: true }],
      phoneNumber: null,
      itsMyPhone: false,
      text: null,
      communicationType: this.communicationTypes[0].id,
      ags: [{ value: selectedEntity?.entity.ags, disabled: true }],
      givenName: [{ value: selectedUser?.given_name, disabled: true }],
      familyName: [{ value: selectedUser?.family_name, disabled: true }],
      moduleMarketingGuid: [{ value: MARKETING_CRM_GUID_MAP[this.data.slot], disabled: true }]
    });

    if (this.data.hasAgreementCheck) {
      this.addAgreementControl();
    }

    this.communicationType.valueChanges.pipe(startWith(this.communicationType.value)).subscribe((value: string) => {
      if (value === "phone") {
        this.email.clearValidators();
        this.itsMyPhone.addValidators([Validators.requiredTrue]);
        this.phone.addValidators([Validators.required]);
      } else {
        this.email.addValidators([Validators.required]);
        this.itsMyPhone.clearValidators();
        this.phone.clearValidators();
      }

      this.email.updateValueAndValidity();
      this.itsMyPhone.updateValueAndValidity();
      this.phone.updateValueAndValidity();
    });
  }

  public ngAfterViewInit() {
    this.analytics.scanForForms();
    this.marketingService.prepareForm("#teaserForm");
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public submit(e: SubmitEvent): boolean {
    e.preventDefault();
    this.store
      .pipe(
        select(selectEntity),
        withLatestFrom(this.store.pipe(select(selectUserInfo)), this.contactPerson$),
        take(1),
        switchMap(([{ entity }, userInfo, contactPerson]) =>
          this.leadService.submit({
            ...this.leadForm.getRawValue(),
            ags: entity.ags,
            regionName: entity.name,
            contactEmail: contactPerson.email,
            familyName: userInfo.family_name,
            givenName: userInfo.given_name,
            moduleName: this.translate.instant(`LAUNCHPAD.PLUGINS.${this.data.slot}.name`)
          })
        ),
        tap(() => {
          this.snackbar.success("Success", { duration: 2000 });
          this.analytics.trackTeaserFormSubmit(this.data.slot);
        }),
        catchError(() => of(this.snackbar.error("Failed", { duration: 2000 })))
      )
      .subscribe(() => {
        this.dialogRef.close();
      });
    return false;
  }

  public downloadFlyer(link: any) {
    this.filesService.getPdf(link);
  }

  public close(): void {
    this.dialogRef.close();
  }

  public openDialog() {
    this.dialogsService.openPolicyDialog("dataProtection");
  }

  public trackEmail(): void {
    this.analytics.trackTeaserEmailClick(this.data.slot);
  }

  private addAgreementControl(): void {
    this.leadForm.addControl("agreement", this.fb.control(false, Validators.requiredTrue));
  }

  private getContactPerson(moduleId: CrmModule): Observable<IContactPerson> {
    if ([CrmModule.QuickCheck, CrmModule.KWP].includes(moduleId)) {
      return of({ email: "waermeplanung@Netze-BW.de" } as IContactPerson);
    }
    return this.store.pipe(select(selectRegionServiceContactPerson));
  }
}
